import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Sparkles, User } from 'lucide-react';
import { ClientPicker } from '@/components/ClientPicker';
import { ContactPicker } from '@/components/ContactPicker';
import { ThemeToggle } from '@/components/ThemeToggle';
import { createPackage, createProject } from '@/lib/api';
import type { Client, ClientContact } from '@/lib/types';

const inputCls =
  'w-full rounded-md border border-bmade-200 bg-white px-3 py-2 text-sm text-bmade-950 outline-none focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white';

const labelCls = 'mb-1 block text-xs uppercase tracking-wider text-bmade-600 dark:text-bmade-400';

export function NewProjectWizard() {
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [contact, setContact] = useState<ClientContact | null>(null);
  const [name, setName] = useState('');
  const [packageCount, setPackageCount] = useState(50);
  const [budget, setBudget] = useState(35);
  const [budgetType, setBudgetType] = useState<'incl_btw' | 'excl_btw'>('incl_btw');
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleStart() {
    if (!client) {
      setError('Kies eerst een klant.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const fallbackName = `${client.company_name} pakket ${new Date().getFullYear()}`;
      const project = await createProject({
        client_id: client.id,
        client_contact_id: contact?.id ?? null,
        name: name || fallbackName,
        description: null,
        deadline: deadline || null,
        package_count_planned: packageCount,
      });
      const pkg = await createPackage({
        project_id: project.id,
        client_id: client.id,
        name: project.name,
        quantity: packageCount,
        budget_per_package: budget,
        budget_type: budgetType,
      });
      navigate(`/projecten/${project.id}?package=${pkg.id}&step=gesprek`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aanmaken mislukt');
    } finally {
      setBusy(false);
    }
  }

  const canContinue = client !== null && packageCount > 0 && budget > 0;

  return (
    <div className="relative min-h-screen bg-bmade-50 dark:bg-bmade-950">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-12">
        <header>
          <button
            type="button"
            onClick={() => navigate('/projecten')}
            className="mb-3 inline-flex items-center gap-1 text-xs text-bmade-500 hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white"
          >
            <ArrowLeft className="h-3 w-3" /> Terug naar projecten
          </button>
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-accent-500/15 p-2">
              <Sparkles className="h-5 w-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
                Stap 1 van 8
              </div>
              <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">
                Nieuw pakket-project
              </h1>
            </div>
          </div>
          <p className="mt-2 text-sm text-bmade-600 dark:text-bmade-400">
            Begin met de klant en de basis-aantallen. Daarna komt het gesprek, samenvatting en tips,
            en daarna het samenstellen van het pakket.
          </p>
        </header>

        <Stepper currentIdx={0} />

        <div className="space-y-5 rounded-2xl border border-bmade-200 bg-white p-6 shadow-sm dark:border-bmade-800 dark:bg-bmade-900/60">
          <section>
            <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-bmade-700 dark:text-bmade-300">
              <User className="h-3 w-3" /> Klant
            </div>
            <ClientPicker
              value={client}
              onChange={(c) => {
                setClient(c);
                setContact(null); // reset bij nieuwe klant
              }}
              placeholder="Zoek klant..."
              autoFocus
            />
            {client && (
              <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">
                ✓ Gekoppeld aan {client.company_name}
              </p>
            )}
          </section>

          {client && (
            <section>
              <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-bmade-700 dark:text-bmade-300">
                <User className="h-3 w-3" /> Contactpersoon
              </div>
              <ContactPicker clientId={client.id} value={contact} onChange={setContact} />
            </section>
          )}

          <section>
            <label className={labelCls}>Project-naam (optioneel)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                client
                  ? `${client.company_name} pakket ${new Date().getFullYear()}`
                  : 'bv. Test BV kerst 2026'
              }
              className={inputCls}
            />
          </section>

          <div className="grid grid-cols-2 gap-3">
            <section>
              <label className={labelCls}>Aantal pakketten</label>
              <input
                type="number"
                min={1}
                value={packageCount}
                onChange={(e) => setPackageCount(parseInt(e.target.value) || 1)}
                className={inputCls}
              />
            </section>
            <section>
              <label className={labelCls}>Deadline</label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className={inputCls}
              />
            </section>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <section>
              <label className={labelCls}>Budget per pakket</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={budget}
                onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
                className={inputCls}
              />
            </section>
            <section>
              <label className={labelCls}>BTW</label>
              <select
                value={budgetType}
                onChange={(e) => setBudgetType(e.target.value as 'incl_btw' | 'excl_btw')}
                className={inputCls}
              >
                <option value="incl_btw">Incl. BTW</option>
                <option value="excl_btw">Excl. BTW</option>
              </select>
            </section>
          </div>

          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/projecten')}
            className="text-xs text-bmade-500 hover:text-bmade-900 dark:text-bmade-400 dark:hover:text-white"
          >
            Annuleer
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={!canContinue || busy}
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
          >
            {busy ? 'Bezig...' : 'Verder naar gesprek'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const WIZARD_STEPS = [
  'Klant + basis',
  'Gesprek',
  'AI-tips',
  'Verpakking',
  'Gadgets',
  'Drank',
  'Food',
  'Klaar',
];

function Stepper({ currentIdx }: { currentIdx: number }) {
  return (
    <ol className="flex flex-wrap gap-1 text-[10px]">
      {WIZARD_STEPS.map((label, idx) => {
        const active = idx === currentIdx;
        const past = idx < currentIdx;
        return (
          <li
            key={label}
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 ${
              active
                ? 'bg-accent-500/15 text-accent-700 dark:text-accent-300'
                : past
                  ? 'text-bmade-600 dark:text-bmade-400'
                  : 'text-bmade-400'
            }`}
          >
            <span
              className={`flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-semibold ${
                active
                  ? 'bg-accent-500 text-white'
                  : past
                    ? 'bg-bmade-200 text-bmade-700 dark:bg-bmade-700'
                    : 'border border-bmade-300 dark:border-bmade-700'
              }`}
            >
              {idx + 1}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}
